import React, { useEffect, useState } from "react";
import { processQueue, purgeQueueUrl, queueSize } from "../lib/offlineQueue";
import { API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Wifi, WifiOff, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

export default function OfflineBar() {
  const { user } = useAuth();
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [authBlocked, setAuthBlocked] = useState(false);

  const refresh = async () => setPending(await queueSize());

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    const done = () => { refresh(); toast.success("Sincronização em segundo plano concluída"); };
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    window.addEventListener("nafe-sync-done", done);
    refresh();
    const iv = setInterval(refresh, 3000);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      window.removeEventListener("nafe-sync-done", done);
      clearInterval(iv);
    };
  }, []);

  useEffect(() => {
    if (user) setAuthBlocked(false);
  }, [user]);

  useEffect(() => {
    if (online && pending > 0 && !syncing && !authBlocked) {
      sync();
    }
    // eslint-disable-next-line
  }, [online, pending, authBlocked]);

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await processQueue(API);
      if (res.authFailed) {
        setAuthBlocked(true);
        await purgeQueueUrl("/tracking/ping");
        toast.error("Sessão expirada — faça login novamente para sincronizar");
      } else if (res.ok > 0) {
        toast.success(`${res.ok} ocorrência(s) sincronizada(s)`);
      }
      if (res.fail > 0 && !res.authFailed) toast.error(`${res.fail} falhou`);
    } finally {
      setSyncing(false);
      refresh();
    }
  };

  if (online && pending === 0) return null;

  return (
    <div
      className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-50 rounded-2xl shadow-xl px-4 py-2.5 flex items-center gap-2.5 text-xs font-medium ${
        online ? "bg-[var(--brand)] text-white" : "bg-[var(--danger)] text-white"
      }`}
      data-testid="offline-bar"
    >
      {online ? <Wifi size={14} /> : <WifiOff size={14} />}
      <span className="font-mono">
        {online
          ? syncing
            ? `Sincronizando... (${pending})`
            : `${pending} pendente(s) para sincronizar`
          : `Offline — ${pending} pendente(s) na fila`}
      </span>
      {online && (
        <button
          onClick={sync}
          disabled={syncing}
          className="ml-2 border border-white/40 px-2 py-0.5 hover:bg-white/10 flex items-center gap-1"
          data-testid="sync-now"
        >
          <RefreshCcw size={12} className={syncing ? "animate-spin" : ""} /> Sync
        </button>
      )}
    </div>
  );
}
